import React, { Component } from 'react';
import Portal from '~/UI/Portal/Portal';

import defaultStyles from './default.less';

export type Position = 'top' | 'bottom' | 'left' | 'right';
export interface Styles {
	hiddenPopover: string;
	popover: string;
	arrow: string;
	content: string;
}
interface Props {
	children: React.ReactNode;
	width: number | null;
	referenceRect: ClientRect;
	position: Position;
	styles: Styles;
	onClose(event: MouseEvent): void;
	onMouseEnter?(event: React.MouseEvent<HTMLDivElement>): void;
	onMouseLeave?(event: React.MouseEvent<HTMLDivElement>): void;
}

interface State {
	rect: { left: number; top: number; width: number } | null;
	arrowRect: { left: number; top: number; transform?: string } | null;
	hidden: boolean;
}

const ArrowSize = 16;

export default class Popover extends Component<Props, State> {
	static defaultProps = {
		children: null,
		width: null,
		position: 'bottom',
		styles: defaultStyles,
	};

	state = {
		rect: null,
		arrowRect: null,
		hidden: true,
	};

	mounted = false;

	containerElement: HTMLDivElement | null = null;

	componentDidMount() {
		this.mounted = true;
		document.addEventListener('click', this.handleDocumentClick, true);
		this.updateRect();
	}

	componentWillUnmount() {
		this.mounted = false;
		document.removeEventListener('click', this.handleDocumentClick, true);
	}

	componentDidUpdate(prevProps: Props) {
		if (this.state.hidden) {
			setTimeout(() => {
				if (this.mounted) {
					this.setState({ hidden: false });
				}
			}, 0);
		}

		if (
			this.props.children !== prevProps.children ||
			this.props.width !== prevProps.width ||
			this.props.referenceRect !== prevProps.referenceRect ||
			this.props.position !== prevProps.position
		) {
			this.updateRect();
		}
	}

	updateRect() {
		if (this.containerElement === null) {
			return;
		}

		const { width, height } = this.containerElement.getBoundingClientRect();
		const margin = 10;
		const maxLeft = window.innerWidth - width - margin;
		const maxTop = window.innerHeight - height - margin;

		const left = Math.max(0, Math.min(maxLeft, this.calculateLeft(width)));
		const top = Math.max(0, Math.min(maxTop, this.calculateTop(height)));

		this.setState({
			rect: { left, top, width },
			arrowRect: this.calculateArrowRect(left, top, width, height),
		});
	}

	calculateLeft(width: number): number {
		const { position, referenceRect } = this.props;

		switch (position) {
			case 'top':
			case 'bottom':
				return referenceRect.left + referenceRect.width / 2 - width / 2;
			case 'left':
				return referenceRect.left - width;
			case 'right':
				return referenceRect.left + referenceRect.width;
			default:
				return 0;
		}
	}

	calculateTop(height: number): number {
		const { position, referenceRect } = this.props;

		switch (position) {
			case 'top':
				return referenceRect.top - height - (ArrowSize / 2 - referenceRect.height / 2);
			case 'bottom':
				return referenceRect.bottom;
			case 'left':
			case 'right':
				return referenceRect.top + referenceRect.width / 2 - height / 2;
			default:
				return 0;
		}
	}

	calculateArrowRect(
		left: number,
		top: number,
		width: number,
		height: number
	): { left: number; top: number; transform?: string } | null {
		const { referenceRect } = this.props;
		const refLeft = referenceRect.left + referenceRect.width / 2;
		const refTop = referenceRect.top + referenceRect.height / 2;

		const arrowLeft = Math.max(-ArrowSize, Math.min(width, refLeft - left - ArrowSize / 2));
		const arrowTop = Math.max(-ArrowSize, Math.min(height, refTop - top - ArrowSize / 2));

		if (arrowLeft > 0 && arrowLeft < width && arrowTop > 0 && arrowTop < height) {
			return null;
		}
		if (arrowLeft < 0) {
			return {
				left: arrowLeft,
				top: Math.min(height - ArrowSize, arrowTop),
				transform: 'rotate(-90deg)',
			};
		}
		if (arrowLeft >= width) {
			return {
				left: arrowLeft,
				top: Math.min(height - ArrowSize, arrowTop),
				transform: 'rotate(90deg)',
			};
		}
		if (arrowTop < 0) {
			return { left: Math.min(width - ArrowSize, arrowLeft), top: arrowTop };
		}
		if (arrowTop >= height) {
			return {
				left: Math.min(width - ArrowSize, arrowLeft),
				top: arrowTop,
				transform: 'rotate(180deg)',
			};
		}
		return null;
	}

	handleDocumentClick = (event: MouseEvent) => {
		if (
			event.target !== this.containerElement &&
			this.containerElement !== null &&
			event.target instanceof HTMLElement &&
			!this.containerElement.contains(event.target)
		) {
			this.props.onClose(event);
		}
	};

	render() {
		const {
			props: { width, children, styles, onMouseEnter, onMouseLeave },
			state: { rect, arrowRect, hidden },
		} = this;
		const style =
			rect !== undefined
				? rect || undefined
				: { width: width || undefined, maxWidth: width ? undefined : 600, left: 0, top: 0 };

		return (
			<Portal>
				<div
					className={hidden ? styles.hiddenPopover : styles.popover}
					data-testid='popover'
					ref={(ref) => (this.containerElement = ref)}
					style={style}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				>
					{arrowRect ? <span className={styles.arrow} style={arrowRect} /> : null}
					<div className={styles.content}>{children}</div>
				</div>
			</Portal>
		);
	}
}
